import React, { useState } from "react";
import { IoClose, IoChevronDown } from "react-icons/io5";
import ModalLayout from "../../components/layouts/ModalLayout";

const RECURRENCE_OPTIONS: { label: string; value: "ONCE" | "DAILY" }[] = [
    { label: "One-time", value: "ONCE" },
    { label: "Daily", value: "DAILY" },
    // { label: "Weekly",   value: "WEEKLY" },
    // { label: "Monthly",  value: "MONTHLY" },
];

interface ScheduleTimeModalProps {
    onConfirm: (label: string, scheduleDate: string, scheduleTime: string, type: "ONCE" | "DAILY") => void;
    onClose: () => void;
}

const buildDays = () => {
    const days: { label: string; sub: string; date: Date }[] = [];
    const now = new Date();
    for (let i = 0; i < 3; i++) {
        const d = new Date(now);
        d.setDate(now.getDate() + i);
        days.push({
            label: d.toLocaleDateString("en-US", { weekday: "short" }),
            sub: String(d.getDate()),
            date: d,
        });
    }
    return days;
};

const buildTimeSlots = (selectedDate: Date) => {
    const slots: { label: string; unix: number }[] = [];
    const now = new Date();
    const isToday = selectedDate.toDateString() === now.toDateString();

    const startTime = new Date(selectedDate);

    if (isToday) {
        // First available slot = next 30-minute interval
        const minutes = now.getMinutes();

        if (minutes < 30) {
            startTime.setHours(now.getHours(), 30, 0, 0);
        } else {
            startTime.setHours(now.getHours() + 1, 0, 0, 0);
        }
    } else {
        startTime.setHours(8, 0, 0, 0);
    }

    const endTime = new Date(selectedDate);
    endTime.setHours(22, 0, 0, 0);

    while (startTime <= endTime) {
        const label = startTime.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });

        slots.push({
            label,
            unix: Math.floor(startTime.getTime() / 1000),
        });

        startTime.setMinutes(startTime.getMinutes() + 30);
    }

    return slots;
};

export default function ScheduleTimeModal({ onConfirm, onClose }: ScheduleTimeModalProps) {
    const days = buildDays();

    const [selectedDay, setSelectedDay] = useState(days[0]);
    const [selectedSlot, setSelectedSlot] = useState<{ label: string; unix: number } | null>(null);
    const [recurrence, setRecurrence] = useState<"ONCE" | "DAILY">("ONCE");
    const [recurrenceOpen, setRecurrenceOpen] = useState(false);
    const [showCustomDate, setShowCustomDate] = useState(false);

    const timeSlots = buildTimeSlots(selectedDay.date);

    const handleDaySelect = (day: typeof days[0]) => {
        setSelectedDay(day);
        setSelectedSlot(null);
    };

    const handleCustomDate = (e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = new Date(e.target.value + "T00:00:00");
        if (isNaN(picked.getTime())) return;
        setSelectedDay({
            label: picked.toLocaleDateString("en-US", { weekday: "short" }),
            sub: String(picked.getDate()),
            date: picked,
        });
        setSelectedSlot(null);
        setShowCustomDate(false);
    };

    const selectedRecurrenceLabel =
        RECURRENCE_OPTIONS.find((o) => o.value === recurrence)?.label ?? "One-time";

    const handleConfirm = () => {
        if (!selectedSlot) return;

        // Format: "2026-03-12"
        const d = selectedDay.date;
        const scheduleDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

        // Format: "14:00:00"  (slots are on the hour, so minute/second = 00)
        const slotDate = new Date(selectedSlot.unix * 1000);
        const scheduleTime = `${String(slotDate.getHours()).padStart(2, "0")}:${String(slotDate.getMinutes()).padStart(2, "0")}:00`;

        const dateStr = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
        const label = `${dateStr} ${selectedSlot.label}${recurrence ? ` · ${selectedRecurrenceLabel}` : ""}`;

        onConfirm(label, scheduleDate, scheduleTime, recurrence);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-web-background/40 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col gap-5 overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between px-5 pt-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                            <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                        </div>
                        <div>
                            <p className="font-bold text-gray-900 text-sm leading-tight">Schedule for Later</p>
                            <p className="text-xs text-gray-400">Pick a day and time that suits you</p>
                        </div>
                    </div>
                    <IoClose
                        className="text-xl cursor-pointer text-gray-400 hover:text-red-500 transition"
                        onClick={onClose}
                    />
                </div>

                <div className="px-5 flex flex-col gap-5">

                    {/* Day Picker */}
                    <div className="flex items-center gap-2">
                        {days.map((day) => {
                            const isActive =
                                selectedDay.sub === day.sub && selectedDay.label === day.label;
                            return (
                                <button
                                    key={day.sub + day.label}
                                    onClick={() => handleDaySelect(day)}
                                    className={`flex-1 flex flex-col items-center py-2.5 rounded-xl border text-sm font-semibold transition ${isActive
                                        ? "bg-blue-600 border-blue-600 text-white shadow-md"
                                        : "border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-600"
                                        }`}
                                >
                                    <span className={`text-xs font-medium ${isActive ? "text-blue-100" : "text-gray-400"}`}>
                                        {day.label}
                                    </span>
                                    <span className="text-lg font-bold leading-tight">{day.sub}</span>
                                </button>
                            );
                        })}

                        <span className="text-xs text-gray-400 font-semibold shrink-0">OR</span>

                        {/* Choose custom date */}
                        <div className="relative flex-1">
                            <button
                                onClick={() => setShowCustomDate(!showCustomDate)}
                                className="w-full flex flex-col items-center py-2.5 rounded-xl border border-gray-200 text-gray-500 hover:border-blue-300 hover:text-blue-600 transition text-xs font-semibold gap-0.5"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <rect x="3" y="4" width="18" height="18" rx="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                                Choose date
                            </button>
                            {showCustomDate && (
                                <input
                                    type="date"
                                    min={new Date().toISOString().split("T")[0]}
                                    autoFocus
                                    onChange={handleCustomDate}
                                    className="absolute top-full mt-1 left-0 w-40 border rounded-xl px-2 py-1.5 text-xs shadow-lg z-30 bg-white outline-none focus:border-blue-500"
                                />
                            )}
                        </div>
                    </div>

                    {/* Time Grid */}
                    <div className="flex flex-col gap-2">
                        <p className="text-xs font-bold text-gray-700">Choose a convenient time</p>
                        {timeSlots.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-4">
                                No slots available for today. Please pick another day.
                            </p>
                        ) : (
                            <div className="grid grid-cols-3 gap-2 max-h-44 overflow-y-auto pr-0.5">
                                {timeSlots.map((slot) => {
                                    const isActive = selectedSlot?.unix === slot.unix;
                                    return (
                                        <button
                                            key={slot.unix}
                                            onClick={() => setSelectedSlot(slot)}
                                            className={`py-2.5 rounded-xl text-xs font-semibold border transition ${isActive
                                                ? "bg-blue-600 border-blue-600 text-white shadow-md"
                                                : "border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-600"
                                                }`}
                                        >
                                            {slot.label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Repeat Dropdown */}
                    <div className="relative">
                        <label className="text-xs font-bold text-gray-700 block mb-1.5">Repeat</label>
                        <div
                            onClick={() => setRecurrenceOpen(!recurrenceOpen)}
                            className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-2.5 cursor-pointer hover:border-blue-400 transition"
                        >
                            <span className="text-sm font-semibold text-gray-700">
                                {selectedRecurrenceLabel}
                            </span>
                            <IoChevronDown
                                className={`text-gray-400 transition-transform duration-200 ${recurrenceOpen ? "rotate-180" : ""
                                    }`}
                            />
                        </div>
                        {recurrenceOpen && (
                            <div className="absolute bottom-full mb-1 w-full bg-white border rounded-xl shadow-xl z-30 overflow-hidden">
                                {RECURRENCE_OPTIONS.map((opt) => (
                                    <div
                                        key={opt.value}
                                        onClick={() => {
                                            setRecurrence(opt.value);
                                            setRecurrenceOpen(false);
                                        }}
                                        className={`px-4 py-2.5 text-sm font-semibold cursor-pointer transition ${recurrence === opt.value
                                            ? "bg-blue-600 text-white"
                                            : "hover:bg-blue-50 text-gray-700"
                                            }`}
                                    >
                                        {opt.label}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Confirm Button */}
                <div className="px-5 pb-5">
                    <button
                        disabled={!selectedSlot}
                        onClick={handleConfirm}
                        className="w-full bg-blue-600 text-white rounded-xl py-3 text-sm font-bold hover:bg-blue-700 transition disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
                    >
                        Confirm Schedule
                    </button>
                </div>
            </div>
        </div>
    );
}
